'use client';

import React, { CSSProperties, useEffect, useMemo, useState } from "react";
import ProductMiniEditorCard, {
    ProductData,
} from "../ProductMiniEditorCard/ProductMiniEditorCard";

export type ProductMiniEditorListViewProps = {
    products: ProductData[];
    className?: string;
    currency?: string;
    showImages?: boolean;
    defaultExpandedIds?: string[];
    onProductsChange?: (products: ProductData[]) => void;
    onProductSave?: (product: ProductData, index: number) => void;
};

const getProductCategory = (product: ProductData) =>
    product?.parentId?.trim() || "Uncategorised";

const formatCategoryLabel = (value?: string) => {
    if (!value || value === "Uncategorised") {
        return "Uncategorised";
    }

    return value
        .replace(/^itm-prd-/, "")
        .replace(/-/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getSearchableText = (product: ProductData) => {
    const title = product?.title || "";
    const name = product?.basic?.name || "";
    const subtitle = product?.basic?.subtitle || "";
    const slug = product?.slug || "";
    const description = product?.description || "";
    const parentId = product?.parentId || "";
    const features = Array.isArray(product?.features) ? product.features.join(" ") : "";

    return [title, name, subtitle, slug, description, parentId, features]
        .join(" ")
        .toLowerCase();
};

export default function ProductMiniEditorListView({
                                                      products,
                                                      className = "",
                                                      currency = "AUD",
                                                      showImages = true,
                                                      defaultExpandedIds = [],
                                                      onProductsChange,
                                                      onProductSave,
                                                  }: ProductMiniEditorListViewProps) {
    const [localProducts, setLocalProducts] = useState<ProductData[]>(products || []);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        setLocalProducts(products || []);
    }, [products]);

    const categoryData = useMemo(() => {
        const counts = new Map<string, number>();

        for (const product of localProducts) {
            const category = getProductCategory(product);
            counts.set(category, (counts.get(category) || 0) + 1);
        }

        return Array.from(counts.entries())
            .map(([category, count]) => ({
                category,
                label: formatCategoryLabel(category),
                count,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [localProducts]);

    const totalProducts = localProducts.length;

    const filteredProducts = useMemo(() => {
        let result = localProducts;

        if (selectedCategories.length > 0) {
            result = result.filter((product) =>
                selectedCategories.includes(getProductCategory(product))
            );
        }

        if (searchTerm.trim()) {
            const query = searchTerm.trim().toLowerCase();
            result = result.filter((product) => getSearchableText(product).includes(query));
        }

        return result;
    }, [localProducts, searchTerm, selectedCategories]);

    const filteredCount = filteredProducts.length;

    const toggleCategory = (category: string) => {
        setSelectedCategories((current) =>
            current.includes(category)
                ? current.filter((item) => item !== category)
                : [...current, category]
        );
    };

    const clearFilters = () => {
        setSelectedCategories([]);
        setSearchTerm("");
    };

    const selectAllCategories = () => {
        setSelectedCategories(categoryData.map((item) => item.category));
    };

    const handleProductChange = (nextProduct: ProductData, index: number) => {
        setLocalProducts((current) => {
            const nextProducts = current.map((product, productIndex) =>
                productIndex === index ? nextProduct : product
            );

            onProductsChange?.(nextProducts);
            return nextProducts;
        });
    };

    const styles: Record<string, CSSProperties> = {
        root: {
            width: "100%",
            display: "grid",
            gap: 18,
            fontFamily:
                'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        },
        toolbar: {
            display: "grid",
            gap: 16,
            padding: 18,
            borderRadius: 20,
            border: "1px solid rgba(17,17,17,0.08)",
            background: "#ffffff",
            boxShadow: "0 6px 24px rgba(0,0,0,0.04)",
        },
        summaryRow: {
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            alignItems: "center",
            justifyContent: "space-between",
        },
        summaryLeft: {
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            alignItems: "center",
        },
        statPill: {
            display: "inline-flex",
            alignItems: "center",
            minHeight: 34,
            padding: "8px 12px",
            borderRadius: 999,
            background: "#fafafa",
            border: "1px solid rgba(17,17,17,0.08)",
            fontSize: 13,
            lineHeight: 1.2,
            fontWeight: 700,
            color: "#111111",
        },
        actions: {
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            alignItems: "center",
        },
        button: {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 38,
            padding: "8px 12px",
            borderRadius: 12,
            border: "1px solid rgba(17,17,17,0.12)",
            background: "#ffffff",
            color: "#111111",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
        },
        searchWrap: {
            display: "grid",
            gap: 8,
        },
        searchInput: {
            width: "100%",
            minHeight: 42,
            padding: "10px 12px",
            borderRadius: 12,
            border: "1px solid rgba(17,17,17,0.12)",
            background: "#ffffff",
            color: "#111111",
            fontSize: 14,
            boxSizing: "border-box",
        },
        categorySection: {
            display: "grid",
            gap: 12,
        },
        categoryTitle: {
            margin: 0,
            fontSize: 13,
            lineHeight: 1.2,
            fontWeight: 800,
            color: "rgba(17,17,17,0.65)",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
        },
        categoryGrid: {
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 10,
        },
        checkboxCard: {
            display: "flex",
            alignItems: "center",
            gap: 10,
            minHeight: 52,
            padding: "10px 12px",
            borderRadius: 14,
            border: "1px solid rgba(17,17,17,0.08)",
            background: "#fcfcfc",
        },
        checkbox: {
            width: 16,
            height: 16,
            cursor: "pointer",
            flexShrink: 0,
        },
        checkboxLabelWrap: {
            display: "grid",
            gap: 2,
            minWidth: 0,
            flex: 1,
            cursor: "pointer",
        },
        checkboxLabel: {
            fontSize: 14,
            lineHeight: 1.25,
            fontWeight: 700,
            color: "#111111",
        },
        checkboxMeta: {
            fontSize: 12,
            lineHeight: 1.2,
            color: "rgba(17,17,17,0.58)",
            fontWeight: 600,
        },
        listWrap: {
            display: "grid",
            gap: 14,
        },
        emptyState: {
            padding: 24,
            borderRadius: 18,
            border: "1px dashed rgba(17,17,17,0.12)",
            background: "#ffffff",
            color: "rgba(17,17,17,0.58)",
            fontSize: 14,
            lineHeight: 1.5,
            textAlign: "center" as const,
        },
    };

    return (
        <section style={styles.root} className={className}>
            <div style={styles.toolbar}>
                <div style={styles.summaryRow}>
                    <div style={styles.summaryLeft}>
                        <span style={styles.statPill}>
                            Total Products: {totalProducts}
                        </span>
                        <span style={styles.statPill}>
                            Categories: {categoryData.length}
                        </span>
                        <span style={styles.statPill}>
                            Showing: {filteredCount}
                        </span>
                    </div>

                    <div style={styles.actions}>
                        <button
                            type="button"
                            onClick={selectAllCategories}
                            style={styles.button}
                        >
                            Select All
                        </button>

                        <button
                            type="button"
                            onClick={clearFilters}
                            style={styles.button}
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>

                <div style={styles.searchWrap}>
                    <input
                        type="text"
                        placeholder="Search products..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={styles.searchInput}
                    />
                </div>

                <div style={styles.categorySection}>
                    <h2 style={styles.categoryTitle}>Filter by category</h2>

                    <div style={styles.categoryGrid}>
                        {categoryData.map((item) => {
                            const checked = selectedCategories.includes(item.category);
                            const inputId = `category-filter-${item.category
                                .toLowerCase()
                                .replace(/[^a-z0-9]+/g, "-")}`;

                            return (
                                <div key={item.category} style={styles.checkboxCard}>
                                    <input
                                        id={inputId}
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleCategory(item.category)}
                                        style={styles.checkbox}
                                    />

                                    <label htmlFor={inputId} style={styles.checkboxLabelWrap}>
                                        <span style={styles.checkboxLabel}>{item.label}</span>
                                        <span style={styles.checkboxMeta}>
                                            {item.count} product{item.count === 1 ? "" : "s"}
                                        </span>
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div style={styles.listWrap}>
                {filteredProducts.length > 0 ? (
                    filteredProducts.map((product, index) => {
                        const productId = product?.id || `product-${index}`;
                        const isExpandedByDefault = defaultExpandedIds.includes(productId);

                        return (
                            <ProductMiniEditorCard
                                key={productId}
                                product={product}
                                currency={currency}
                                showImage={showImages}
                                defaultExpanded={isExpandedByDefault}
                                onChange={(nextProduct) => {
                                    const originalIndex = localProducts.findIndex(
                                        (item) => item.id === product.id
                                    );
                                    const resolvedIndex = originalIndex >= 0 ? originalIndex : index;
                                    handleProductChange(nextProduct, resolvedIndex);
                                }}
                                onSave={(nextProduct) => {
                                    const originalIndex = localProducts.findIndex(
                                        (item) => item.id === product.id
                                    );
                                    const resolvedIndex = originalIndex >= 0 ? originalIndex : index;
                                    onProductSave?.(nextProduct, resolvedIndex);
                                }}
                            />
                        );
                    })
                ) : (
                    <div style={styles.emptyState}>
                        No products match the selected filters.
                    </div>
                )}
            </div>
        </section>
    );
}
